import React, { useMemo } from 'react';
import Confetti from 'react-confetti';
import './Step2CompleteConfetti.css';

const OOLA_F_COLORS = [
    '#15C285',
    '#ABD86D',
    '#FBBF53',
    '#F26636',
    '#EC0F47',
    '#700460',
    '#022B7A',
];

const ARC_VARIANTS = ['full', 'quarter', 'half'];

export function prefersReducedMotion() {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function drawOolaArc(ctx, mobile) {
    const variant =
        this.arcVariant ||
        ARC_VARIANTS[Math.floor(Math.random() * ARC_VARIANTS.length)];
    this.arcVariant = variant;
    ctx.beginPath();
    ctx.lineWidth = mobile ? 4 : 6;
    ctx.lineCap = 'round';
    const r = mobile ? 11 : 16;
    switch (variant) {
        case 'full':
            ctx.arc(0, 0, r, 0, Math.PI * 2, false);
            break;
        case 'quarter':
            ctx.arc(0, 0, r, 0, Math.PI / 2, false);
            break;
        default:
            ctx.arc(0, 0, r, 0, Math.PI, false);
            break;
    }
    ctx.stroke();
    ctx.closePath();
}

function buildCssParticles(count = 72) {
    return Array.from({ length: count }, (_, i) => ({
        id: i,
        variant: ARC_VARIANTS[i % ARC_VARIANTS.length],
        color: OOLA_F_COLORS[i % OOLA_F_COLORS.length],
        angle: (360 / count) * i + ((i * 11) % 20),
        dist: 110 + (i % 9) * 24,
        delay: (i % 6) * 0.014,
        size: 6 + (i % 4) * 2,
        spin: (i % 2 === 0 ? 1 : -1) * (30 + (i % 5) * 16),
        dur: 1.2 + (i % 4) * 0.1,
    }));
}

function readBurstOrigin(anchorRef) {
    const el = anchorRef?.current;
    const w = window.innerWidth;
    const h = window.innerHeight;
    if (el) {
        const rect = el.getBoundingClientRect();
        return {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
            source: {
                w: 32,
                h: 32,
                x: rect.left + rect.width / 2 - 16,
                y: rect.top + rect.height / 2 - 16,
            },
        };
    }
    return {
        x: w * 0.75,
        y: h * 0.48,
        source: { w: 32, h: 32, x: w * 0.75 - 16, y: h * 0.48 - 16 },
    };
}

const Step2CompleteConfettiBurst = ({ anchorRef, mobile = false }) => {
    const reduced = useMemo(() => prefersReducedMotion(), []);
    const cssParticles = useMemo(
        () => (reduced ? [] : buildCssParticles(72)),
        [reduced]
    );
    const origin = readBurstOrigin(anchorRef);
    const width = window.innerWidth;
    const height = window.innerHeight;

    const drawShape = function drawShape(ctx) {
        drawOolaArc.call(this, ctx, mobile);
    };

    return (
        <div className="step2CompleteConfettiPortal" aria-hidden="true">
            {reduced ? (
                <span
                    className="step2CompleteConfetti step2CompleteConfetti--reduced"
                    style={{ left: origin.x, top: origin.y }}
                />
            ) : (
                <>
                    <span
                        className="step2CompleteConfetti"
                        style={{ left: origin.x, top: origin.y }}
                        aria-hidden="true"
                    >
                        {cssParticles.map((p) => (
                            <span
                                key={p.id}
                                className={`step2CompleteConfetti__p step2CompleteConfetti__p--${p.variant}`}
                                style={{
                                    '--a': `${p.angle}deg`,
                                    '--d': `${p.dist}px`,
                                    '--delay': `${p.delay}s`,
                                    '--dur': `${p.dur}s`,
                                    '--c': p.color,
                                    '--s': `${p.size}px`,
                                    '--spin': `${p.spin}deg`,
                                }}
                            />
                        ))}
                    </span>
                    <Confetti
                        width={width}
                        height={height}
                        numberOfPieces={68}
                        recycle={false}
                        colors={OOLA_F_COLORS}
                        confettiSource={origin.source}
                        initialVelocityX={{ min: -48, max: 48 }}
                        initialVelocityY={{ min: -52, max: 32 }}
                        gravity={0.34}
                        friction={0.96}
                        tweenDuration={85}
                        drawShape={drawShape}
                        style={{
                            position: 'fixed',
                            inset: 0,
                            zIndex: 10000,
                            pointerEvents: 'none',
                        }}
                    />
                </>
            )}
        </div>
    );
};

export default Step2CompleteConfettiBurst;
